import { useState } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { FaqList } from '../components/FaqList';
import { SuggestionChips } from '../components/SuggestionChips';
import { capGroups, flattenGroups } from '../lib/faqSelector';
import { useHelp } from '../useHelp';

/**
 * Left pane. From 1280 up: the selector's groups as a list, capped at ~12 with "Show
 * more". Below: the chip row is the list, with an "All N" drawer for the full set.
 * Remounted per context (keyed by the caller) so both toggles reset.
 */
export function FaqPane() {
  const { faqGroups, selectedFaqId, askFaq } = useHelp();
  const [showAll, setShowAll] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const { groups, remaining } = showAll ? { groups: faqGroups, remaining: 0 } : capGroups(faqGroups);
  const all = flattenGroups(faqGroups);
  const pick = (faqId: string) => {
    setDrawerOpen(false);
    askFaq(faqId);
  };

  return (
    <>
      <div className="hidden xl:block xl:h-full">
        <FaqList groups={groups} selectedId={selectedFaqId} onSelect={askFaq} remaining={remaining} onShowMore={() => setShowAll(true)} />
      </div>

      <div className="xl:hidden">
        <div className="px-4 py-3">
          <SuggestionChips
            variant="faq-row"
            label={faqGroups[0].label}
            chips={all.map((faq) => ({ id: faq.id, label: faq.short_label ?? faq.question }))}
            selectedId={selectedFaqId}
            onSelect={pick}
            trailing={
              all.length > 0 ? (
                <button
                  type="button"
                  onClick={() => setDrawerOpen((open) => !open)}
                  aria-expanded={drawerOpen}
                  className="flex h-11 items-center gap-1.5 text-[11.5px] font-bold text-dvaNavy focus-visible:rounded focus-visible:ring-[3px] focus-visible:ring-dvaOrange/35 focus-visible:outline-none"
                >
                  {`All ${all.length}`}
                  {drawerOpen ? (
                    <ChevronUp className="size-3" strokeWidth={2.4} aria-hidden="true" />
                  ) : (
                    <ChevronDown className="size-3" strokeWidth={2.4} aria-hidden="true" />
                  )}
                </button>
              ) : null
            }
          />
        </div>
        {drawerOpen && (
          <div className="max-h-[50vh] overflow-y-auto border-t border-[#e5e7eb] animate-[dvaHelpDrawerIn_220ms_cubic-bezier(.22,1,.36,1)]">
            <FaqList groups={faqGroups} selectedId={selectedFaqId} onSelect={pick} />
          </div>
        )}
      </div>
    </>
  );
}
